import {
  type SectionTemplateFieldSpec,
  type SectionTemplateRegistry,
  sectionTemplateRegistryListSchema,
} from '../schema';

const ALL_DENSITIES = ['light', 'standard', 'rich'] as const;
const ALL_DEVICES = ['balanced', 'mobile_first', 'desktop_first'] as const;
const ALL_INTERACTIONS = ['static', 'light', 'guided', 'mobile_conversion_focused'] as const;
const TEXT_FRIENDLY_MATERIALS = ['text_first', 'photo_optional'] as const;
function textField(
  name: string,
  description: string,
  options?: Partial<SectionTemplateFieldSpec>,
): SectionTemplateFieldSpec {
  return {
    name,
    kind: 'text',
    required: true,
    allowAiGeneration: true,
    requiresVerifiedSource: false,
    sourceKinds: [],
    description,
    ...options,
  };
}

function textareaField(
  name: string,
  description: string,
  options?: Partial<SectionTemplateFieldSpec>,
): SectionTemplateFieldSpec {
  return {
    name,
    kind: 'textarea',
    required: true,
    allowAiGeneration: true,
    requiresVerifiedSource: false,
    sourceKinds: [],
    description,
    ...options,
  };
}

function imageField(
  name: string,
  description: string,
  options?: Partial<SectionTemplateFieldSpec>,
): SectionTemplateFieldSpec {
  return {
    name,
    kind: 'image',
    required: false,
    allowAiGeneration: false,
    requiresVerifiedSource: false,
    sourceKinds: [],
    description,
    ...options,
  };
}

function visualRequirement(
  assetKind: SectionTemplateRegistry['assetRequirements'][number]['assetKind'],
  purpose: string,
  options?: Partial<SectionTemplateRegistry['assetRequirements'][number]>,
): SectionTemplateRegistry['assetRequirements'][number] {
  return {
    slotName: 'image',
    assetKind,
    required: false,
    purpose,
    generationPolicy: 'reuse_only',
    promptHints: [],
    forbiddenElements: [],
    ...options,
  };
}

function listField(
  name: string,
  kind: SectionTemplateFieldSpec['kind'],
  description: string,
  options?: Partial<SectionTemplateFieldSpec>,
): SectionTemplateFieldSpec {
  return {
    name,
    kind,
    required: true,
    allowAiGeneration: true,
    requiresVerifiedSource: false,
    sourceKinds: [],
    description,
    ...options,
  };
}

function rankingSignals(
  familyScore: number,
  appealScore: number,
  deviceScore: number,
  densityScore: number,
  toneScore: number,
  materialScore: number,
) {
  return {
    familyScore,
    appealScore,
    deviceScore,
    densityScore,
    toneScore,
    materialScore,
  };
}

export const SECTION_TEMPLATE_REGISTRIES = sectionTemplateRegistryListSchema.parse([
  {
    id: 'hero_b2b_centered',
    label: 'Hero Centered',
    description: '比較検討中の法人に向けて、対象・価値・安心材料を一読で伝えるBtoB hero。',
    version: 1,
    status: 'active',
    priority: 92,
    tags: ['hero', 'generic'],
    sectionType: 'hero',
    layoutType: 'centered_copy',
    heroPattern: 'trust_authority',
    supportedFamilies: ['btob_inquiry', 'regional_service_inquiry', 'education_application', 'recruitment_entry', 'seminar_application'],
    supportedAppealStyles: ['problem_solution', 'trust_relief', 'authority_expert', 'story_empathy'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: [...ALL_DENSITIES],
    supportedTones: ['trust', 'corporate', 'modern', 'natural_warm'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'ui_capture_required',
      'person_photo_required',
    ],
    supportedInteractions: ['static', 'light', 'guided'],
    requiredFields: [
      textField('headline', '主見出し'),
      textareaField('subheadline', '補足見出し'),
      textField('primaryCtaLabel', '主 CTA 文言'),
    ],
    optionalFields: [imageField('image', '補助画像')],
    assetRequirements: [
      visualRequirement('service_photo', '信頼感を補う補助ビジュアル', {
        generationPolicy: 'diagram_only',
        promptHints: [
          'BtoB向けの落ち着いた抽象ビジュアル',
          '余白を残したミニマルな構図',
          'サービスの流れや整理された印象を想起させる',
        ],
        forbiddenElements: ['実在企業ロゴ', 'UIの具体再現', '判読できる文字'],
      }),
    ],
    contentRules: {
      maxHeadingLength: 30,
      maxBodyLength: 120,
      toneNotes: [
        '誰向けのサービスかと得られる成果を冒頭2行で明快に伝える',
        '法人の比較検討で欲しい安心材料を1つだけ先頭に置く',
        '資料請求LPでは、この資料で何が分かるかまで自然に想像できる導入にする',
      ],
      prohibitedPatterns: ['抽象表現だけで終える', '会社紹介だけで hero を埋める'],
    },
    emptyStateRules: {
      policy: 'omit_optional_fields',
      omitFieldNames: ['image'],
    },
    rankingSignals: rankingSignals(3, 3, 2, 2, 2, 3),
  },
  {
    id: 'hero_saas_split_visual',
    label: 'Hero SaaS Split Visual',
    description: '担当者の業務課題、導入後の変化、UI価値を一画面で理解させるSaaS hero。',
    version: 1,
    status: 'active',
    priority: 95,
    tags: ['hero', 'saas'],
    sectionType: 'hero',
    layoutType: 'split_visual',
    heroPattern: 'feature_showcase',
    supportedFamilies: ['saas_ai_inquiry', 'btob_inquiry'],
    supportedAppealStyles: ['feature_understanding', 'problem_solution'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['standard', 'rich'],
    supportedTones: ['modern', 'corporate', 'trust'],
    supportedMaterialDependency: ['text_first', 'photo_optional', 'ui_capture_required'],
    supportedInteractions: ['static', 'light', 'mobile_conversion_focused'],
    requiredFields: [
      textField('headline', '主見出し'),
      textareaField('subheadline', '補足見出し'),
      textField('primaryCtaLabel', '主 CTA 文言'),
    ],
    optionalFields: [
      imageField('image', 'UI キャプチャまたは図解', {
        sourceKinds: ['ui_capture', 'service_photo'],
      }),
    ],
    assetRequirements: [
      visualRequirement('ui_capture', 'プロダクト理解を早める', {
        generationPolicy: 'diagram_only',
        promptHints: [
          '実在UIの複製ではなく概念的な管理画面イメージ',
          '複数のカードやパネルで整理された SaaS ダッシュボード風',
          'モダンで信頼感のあるプロダクト紹介ビジュアル',
        ],
        forbiddenElements: ['実在UIの再現', '判読できる文字', '企業ロゴ'],
      }),
    ],
    contentRules: {
      maxHeadingLength: 28,
      maxBodyLength: 108,
      toneNotes: [
        '担当者の役割と抱えている業務課題を最初に明示する',
        '導入後に何がどれだけ楽になるかをUIイメージと結びつけて書く',
      ],
      prohibitedPatterns: ['機能名の羅列だけにしない', '抽象的なDX表現だけで終える'],
    },
    emptyStateRules: {
      policy: 'omit_optional_fields',
      omitFieldNames: ['image'],
    },
    rankingSignals: rankingSignals(5, 5, 3, 3, 4, 5),
  },
  {
    id: 'hero_regional_service',
    label: 'Hero Regional Service',
    description: '不用品回収・引越し・便利屋など全国チェーン地域サービス向け。大きな電話番号と複数CTAで相談導線を強める hero。',
    version: 1,
    status: 'active',
    priority: 96,
    tags: ['hero', 'regional', 'service_chain'],
    sectionType: 'hero',
    layoutType: 'local_visual',
    heroPattern: 'trust_authority',
    supportedFamilies: ['regional_service_inquiry'],
    supportedAppealStyles: ['trust_relief', 'benefit_future', 'offer_push'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['standard', 'rich'],
    supportedTones: ['trust', 'friendly'],
    supportedMaterialDependency: ['photo_optional', 'photo_required'],
    supportedInteractions: ['static', 'light', 'mobile_conversion_focused'],
    requiredFields: [
      textField('headline', '主見出し'),
      textareaField('subheadline', '補足見出し'),
      textField('primaryCtaLabel', '主 CTA 文言'),
    ],
    optionalFields: [
      imageField('image', '作業員・現場写真', {
        sourceKinds: ['service_photo', 'location_photo'],
      }),
    ],
    assetRequirements: [
      visualRequirement('service_photo', '担当者の顔と作業姿で安心感を作る', {
        generationPolicy: 'ai_allowed',
        promptHints: [
          'ユニフォーム姿のスタッフ',
          '清潔感のある作業現場',
          '親しみやすく信頼できる印象',
        ],
        forbiddenElements: ['実在企業ロゴ', '判読できる看板文字'],
      }),
    ],
    contentRules: {
      maxHeadingLength: 28,
      maxBodyLength: 96,
      toneNotes: [
        '全国チェーンの信頼感と地元の親しみを両立',
        '電話番号と相談導線を主役に置く',
      ],
      prohibitedPatterns: ['長い説明文で電話導線を埋める', '冷たい高級志向に振る'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'hero_b2b_centered',
    },
    rankingSignals: rankingSignals(4, 5, 5, 4, 3, 5),
  },
  {
    id: 'hero_store_local_visual',
    label: 'Hero Store Local Visual',
    description: '地域性、来店メリット、予約のしやすさを写真と一緒に伝える店舗向け hero。',
    version: 1,
    status: 'active',
    priority: 92,
    tags: ['hero', 'store'],
    sectionType: 'hero',
    layoutType: 'local_visual',
    heroPattern: 'benefit_first',
    supportedFamilies: ['store_reservation', 'regional_service_inquiry'],
    supportedAppealStyles: ['benefit_future', 'trust_relief', 'story_empathy'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard', 'rich'],
    supportedTones: ['friendly', 'natural_warm', 'bright_clean'],
    supportedMaterialDependency: ['photo_optional', 'photo_required'],
    supportedInteractions: ['static', 'light', 'mobile_conversion_focused'],
    requiredFields: [
      textField('headline', '主見出し'),
      textareaField('subheadline', '補足見出し'),
      textField('primaryCtaLabel', '主 CTA 文言'),
    ],
    optionalFields: [
      imageField('image', '店舗またはサービス写真', {
        sourceKinds: ['location_photo', 'service_photo'],
      }),
    ],
    assetRequirements: [
      visualRequirement('location_photo', '来店前の安心感を作る', {
        generationPolicy: 'ai_allowed',
        promptHints: [
          '清潔感があり入りやすい店舗やサービス空間',
          '自然光が入る明るい雰囲気',
          '地域サービスらしい親しみと安心感',
        ],
        forbiddenElements: ['実在店舗名', '判読できる看板文字', '企業ロゴ'],
      }),
    ],
    contentRules: {
      maxHeadingLength: 28,
      maxBodyLength: 96,
      toneNotes: [
        '来店後のうれしい変化と相談しやすさを短く伝える',
        '地域名や通いやすさなど予約判断に効く情報を1つ添える',
      ],
      prohibitedPatterns: ['CTA より前に情報を詰め込みすぎない', '店側の説明だけで終える'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'hero_b2b_centered',
    },
    rankingSignals: rankingSignals(5, 4, 4, 3, 4, 4),
  },
  {
    id: 'hero_product_focus',
    label: 'Hero Product Focus',
    description: '商品カテゴリー、悩み、使用後ベネフィット、初回オファーを短く伝えるD2C hero。',
    version: 1,
    status: 'active',
    priority: 95,
    tags: ['hero', 'product'],
    sectionType: 'hero',
    layoutType: 'product_focus',
    heroPattern: 'offer_urgency',
    supportedFamilies: ['d2c_purchase'],
    supportedAppealStyles: ['benefit_future', 'offer_push', 'story_empathy'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['premium', 'bright_clean', 'friendly'],
    supportedMaterialDependency: ['photo_optional', 'photo_required'],
    supportedInteractions: ['static', 'light', 'mobile_conversion_focused'],
    requiredFields: [
      textField('headline', '主見出し'),
      textareaField('subheadline', '補足見出し'),
      textField('primaryCtaLabel', '主 CTA 文言'),
    ],
    optionalFields: [
      imageField('image', '商品写真', {
        sourceKinds: ['service_photo'],
      }),
    ],
    assetRequirements: [
      visualRequirement('service_photo', '商品価値を一瞬で伝える', {
        generationPolicy: 'reuse_only',
        promptHints: ['実物の商品写真を優先する'],
        forbiddenElements: ['実在商品の捏造', 'ロゴの生成', 'パッケージ文字の捏造'],
      }),
    ],
    contentRules: {
      maxHeadingLength: 24,
      maxBodyLength: 88,
      toneNotes: [
        '悩みと商品カテゴリを同時に見せて使用後のベネフィットへつなげる',
        '初回購入や限定オファーがある場合は強すぎない温度で添える',
      ],
      prohibitedPatterns: ['長い機能説明を hero に入れない', '価格訴求だけを主役にしない'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'hero_b2b_centered',
    },
    rankingSignals: rankingSignals(5, 4, 4, 3, 5, 5),
  },
  {
    id: 'hero_people_split',
    label: 'Hero People Split',
    description: '誰が提供するか、どんな想い・専門性があるかを最初に伝える人物訴求 hero。',
    version: 1,
    status: 'active',
    priority: 90,
    tags: ['hero', 'people'],
    sectionType: 'hero',
    layoutType: 'people_split',
    heroPattern: 'culture_people',
    supportedFamilies: ['recruitment_entry', 'seminar_application', 'education_application'],
    supportedAppealStyles: ['people_culture', 'story_empathy', 'authority_expert', 'trust_relief'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard', 'rich'],
    supportedTones: ['trust', 'modern', 'natural_warm', 'friendly'],
    supportedMaterialDependency: ['text_first', 'photo_optional', 'person_photo_required'],
    supportedInteractions: ['static', 'light', 'guided'],
    requiredFields: [
      textField('headline', '主見出し'),
      textareaField('subheadline', '補足見出し'),
      textField('primaryCtaLabel', '主 CTA 文言'),
    ],
    optionalFields: [
      imageField('image', '人物写真', {
        sourceKinds: ['person_photo', 'team_photo'],
      }),
    ],
    assetRequirements: [
      visualRequirement('person_photo', '誰が提供するかを一目で伝える', {
        generationPolicy: 'reuse_only',
        promptHints: ['本人写真やチーム写真を優先する'],
        forbiddenElements: ['実在人物の捏造', '肩書きの文字入れ'],
      }),
    ],
    contentRules: {
      maxHeadingLength: 28,
      maxBodyLength: 104,
      toneNotes: [
        '登壇者・講師・採用対象など、誰と関わるのかが一目で分かるようにする',
        'ミッションや専門性が応募・申込理由につながる流れで書く',
      ],
      prohibitedPatterns: ['人物訴求と証拠訴求を同じ文で混ぜすぎない', '肩書きの羅列だけで終える'],
    },
    emptyStateRules: {
      policy: 'omit_optional_fields',
      omitFieldNames: ['image'],
    },
    rankingSignals: rankingSignals(4, 5, 2, 2, 3, 4),
  },
  {
    id: 'hero_problem_first',
    label: 'Hero Problem First',
    description: '読み手の悩みを先に言語化し、その直後に解決の方向性とCTAを置く hero。',
    version: 1,
    status: 'active',
    priority: 86,
    tags: ['hero', 'problem'],
    sectionType: 'hero',
    layoutType: 'centered_copy',
    heroPattern: 'problem_first',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry', 'regional_service_inquiry'],
    supportedAppealStyles: ['problem_solution', 'trust_relief', 'feature_understanding'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: [...ALL_DENSITIES],
    supportedTones: ['trust', 'corporate', 'modern', 'bright_clean'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'photo_required',
      'ui_capture_required',
      'person_photo_required',
    ],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('headline', '課題を提起する主見出し'),
      textareaField('subheadline', '課題の補足と解決の予感'),
      textField('primaryCtaLabel', '主 CTA 文言'),
    ],
    optionalFields: [imageField('image', '補助画像')],
    assetRequirements: [
      visualRequirement('service_photo', '課題と解決の方向性を補助するビジュアル', {
        generationPolicy: 'diagram_only',
        promptHints: [
          '抽象的な課題整理や改善のイメージ',
          '情報が整っていく印象の図解風ビジュアル',
          '煽りすぎず落ち着いたトーン',
        ],
        forbiddenElements: ['恐怖訴求の強い表現', '判読できる文字', '実在企業ロゴ'],
      }),
    ],
    contentRules: {
      maxHeadingLength: 30,
      maxBodyLength: 108,
      toneNotes: [
        'ターゲットが自分ごと化できる具体的な悩みを冒頭に置く',
        '課題提起の直後に解決の見通しと次の行動を示す',
        '資料請求LPでは課題の煽りすぎを避け、比較検討の文脈へつなげる',
      ],
      prohibitedPatterns: ['いきなり自社の強みを語る', '抽象的すぎる課題提起'],
    },
    emptyStateRules: {
      policy: 'omit_optional_fields',
      omitFieldNames: ['image'],
    },
    rankingSignals: rankingSignals(3, 5, 2, 2, 3, 3),
  },
  {
    id: 'hero_empathy_story',
    label: 'Hero Empathy Story',
    description: '不安や迷いに寄り添い、利用後の前向きな変化を静かに想像させる hero。',
    version: 1,
    status: 'active',
    priority: 85,
    tags: ['hero', 'empathy'],
    sectionType: 'hero',
    layoutType: 'centered_copy',
    heroPattern: 'empathy_story',
    supportedFamilies: ['regional_service_inquiry', 'store_reservation', 'education_application', 'recruitment_entry'],
    supportedAppealStyles: ['story_empathy', 'benefit_future', 'people_culture'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['friendly', 'natural_warm', 'bright_clean'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS, 'photo_optional'],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('headline', '共感を呼ぶ主見出し'),
      textareaField('subheadline', 'ストーリーの導入と希望'),
      textField('primaryCtaLabel', '主 CTA 文言'),
    ],
    optionalFields: [
      imageField('image', '雰囲気を伝える写真', {
        sourceKinds: ['service_photo', 'person_photo'],
      }),
    ],
    assetRequirements: [
      visualRequirement('service_photo', '前向きな変化を静かに想起させる雰囲気づくり', {
        generationPolicy: 'ai_allowed',
        promptHints: [
          'やわらかい自然光と落ち着いた生活感',
          '具体人物に寄せすぎない情緒的なシーン',
          '安心感と希望を感じる穏やかな空気',
        ],
        forbiddenElements: ['特定個人が識別できる顔', '判読できる文字', '企業ロゴ'],
      }),
    ],
    contentRules: {
      maxHeadingLength: 28,
      maxBodyLength: 104,
      toneNotes: [
        '読み手の感情に寄り添い、前向きな変化の入り口を短く見せる',
        'やさしい温度感を保ちながら最終的には行動理由へ着地させる',
      ],
      prohibitedPatterns: ['スペック訴求から入る', '長すぎる物語', '感情だけで締める'],
    },
    emptyStateRules: {
      policy: 'omit_optional_fields',
      omitFieldNames: ['image'],
    },
    rankingSignals: rankingSignals(3, 5, 2, 2, 4, 3),
  },
  {
    id: 'problem_b2b_structured',
    label: 'Problem Structured',
    description: '課題を論理的に整理して見せる problem section。',
    version: 1,
    status: 'active',
    priority: 97,
    tags: ['problem', 'b2b'],
    sectionType: 'problem',
    layoutType: 'structured_cards',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry'],
    supportedAppealStyles: ['problem_solution', 'trust_relief'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['standard', 'rich'],
    supportedTones: ['trust', 'corporate', 'modern', 'bright_clean'],
    supportedMaterialDependency: ['text_first', 'photo_optional', 'ui_capture_required'],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '課題項目', {
        minItems: 3,
        maxItems: 4,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 4,
      maxHeadingLength: 30,
      maxBodyLength: 80,
      toneNotes: [
        '課題を曖昧な悩みではなく業務上の困りごととして書く',
        '資料請求後の比較検討で社内共有しやすい言葉を優先する',
      ],
      prohibitedPatterns: ['solution と同じ話を繰り返す', '誰の課題かが見えない抽象表現'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 5, 2, 3, 3, 3),
  },
  {
    id: 'problem_story_intro',
    label: 'Problem Story Intro',
    description: '感情移入を作るストーリー寄りの problem section。',
    version: 1,
    status: 'active',
    priority: 88,
    tags: ['problem', 'story'],
    sectionType: 'problem',
    layoutType: 'story_intro',
    supportedFamilies: ['regional_service_inquiry', 'd2c_purchase', 'recruitment_entry'],
    supportedAppealStyles: ['story_empathy', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['friendly', 'natural_warm', 'premium'],
    supportedMaterialDependency: ['text_first', 'photo_optional'],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '共感項目', {
        minItems: 2,
        maxItems: 3,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 2,
      maxItems: 3,
      maxHeadingLength: 28,
      maxBodyLength: 72,
      toneNotes: ['読み手の日常に近い言葉で書く'],
      prohibitedPatterns: ['大げさな恐怖訴求'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(4, 5, 2, 2, 3, 2),
  },
  {
    id: 'solution_visual_diagram',
    label: 'Solution Visual Diagram',
    description: '解決の流れや仕組みを図解的に伝える section。',
    version: 1,
    status: 'active',
    priority: 95,
    tags: ['solution'],
    sectionType: 'solution',
    layoutType: 'diagram_steps',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry', 'regional_service_inquiry'],
    supportedAppealStyles: ['problem_solution', 'feature_understanding'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['standard', 'rich'],
    supportedTones: ['trust', 'corporate', 'modern', 'natural_warm'],
    supportedMaterialDependency: ['text_first', 'photo_optional', 'ui_capture_required'],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '解決ステップ', {
        minItems: 3,
        maxItems: 4,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [imageField('diagram', '補助図解', { sourceKinds: ['ui_capture', 'service_photo'] })],
    assetRequirements: [
      visualRequirement('ui_capture', '解決の全体像を一目で理解させる補助図解', {
        slotName: 'diagram',
        generationPolicy: 'diagram_only',
        promptHints: [
          '解決の流れや仕組みを整理した抽象図解',
          '実在UIの複製ではなく構成理解を助ける概念図',
          'テキストを邪魔しない整理された横長構図',
        ],
        forbiddenElements: ['実在UIの再現', '判読できる文字', '企業ロゴ'],
      }),
    ],
    contentRules: {
      minItems: 3,
      maxItems: 4,
      maxHeadingLength: 30,
      maxBodyLength: 84,
      toneNotes: [
        '課題との対応関係が見える順序にする',
        '資料を読んだ時に解決の全体像が素早くつかめる構成にする',
      ],
      prohibitedPatterns: ['flow と役割が重なる利用手順を書きすぎない', '解決の全体像が見えない断片的な並び'],
    },
    emptyStateRules: {
      policy: 'omit_optional_fields',
      omitFieldNames: ['diagram'],
    },
    rankingSignals: rankingSignals(5, 5, 2, 3, 4, 4),
  },
  {
    id: 'services_menu_blocks',
    label: 'Services Menu Blocks',
    description: '相談・施術・支援範囲など、提供メニューを具体的に把握させる services section。',
    version: 1,
    status: 'active',
    priority: 95,
    tags: ['services'],
    sectionType: 'services',
    layoutType: 'menu_blocks',
    supportedFamilies: ['btob_inquiry', 'regional_service_inquiry', 'store_reservation'],
    supportedAppealStyles: ['problem_solution', 'trust_relief', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard', 'rich'],
    supportedTones: ['trust', 'corporate', 'friendly', 'natural_warm', 'bright_clean'],
    supportedMaterialDependency: ['text_first', 'photo_optional', 'photo_required'],
    supportedInteractions: ['static', 'light', 'mobile_conversion_focused'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', 'サービス項目', {
        minItems: 3,
        maxItems: 6,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 6,
      maxHeadingLength: 28,
      maxBodyLength: 72,
      toneNotes: [
        '提供メニューや支援範囲を用途別に明確に分ける',
        '読み手が自分に必要なメニューを判断できる粒度にする',
        'BtoB資料請求では、比較検討や社内共有で使いやすい言い回しを優先する',
      ],
      prohibitedPatterns: ['feature をそのまま services として書く', '抽象的な価値訴求だけで終える'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 3, 2, 3, 3, 2),
  },
  {
    id: 'features_visual_explain',
    label: 'Features Visual Explain',
    description: '他社と比べた強みや選ぶ理由を、視覚補助つきで理解させる features section。',
    version: 1,
    status: 'active',
    priority: 94,
    tags: ['features'],
    sectionType: 'features',
    layoutType: 'visual_explain',
    supportedFamilies: ['saas_ai_inquiry', 'btob_inquiry', 'd2c_purchase'],
    supportedAppealStyles: ['feature_understanding', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard', 'rich'],
    supportedTones: ['modern', 'corporate', 'trust', 'premium', 'bright_clean'],
    supportedMaterialDependency: [
      'photo_optional',
      'photo_required',
      'ui_capture_required',
      'text_first',
    ],
    supportedInteractions: ['static', 'light', 'mobile_conversion_focused'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '強み項目', {
        minItems: 3,
        maxItems: 4,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [imageField('image', '補助画像', { sourceKinds: ['ui_capture', 'service_photo'] })],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 4,
      maxHeadingLength: 28,
      maxBodyLength: 80,
      toneNotes: [
        '何を提供するかではなく、なぜ選ばれるのかに絞って書く',
        '比較検討中の読み手が差分を理解しやすい順で並べる',
        'BtoB資料請求では、画面や運用イメージと結びつく差分を優先する',
      ],
      prohibitedPatterns: ['services と重複した説明', '抽象的な褒め言葉だけで差別化する'],
    },
    emptyStateRules: {
      policy: 'omit_optional_fields',
      omitFieldNames: ['image'],
    },
    rankingSignals: rankingSignals(5, 5, 2, 3, 4, 4),
  },
  {
    id: 'feature_list_compact',
    label: 'Feature List Compact',
    description: '主要な機能・特徴を一覧で素早く把握させる、比較検討向け feature list。',
    version: 1,
    status: 'active',
    priority: 95,
    tags: ['feature-list'],
    sectionType: 'feature_list',
    layoutType: 'compact_list',
    supportedFamilies: ['saas_ai_inquiry', 'btob_inquiry'],
    supportedAppealStyles: ['feature_understanding', 'problem_solution'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard', 'rich'],
    supportedTones: ['corporate', 'modern', 'trust'],
    supportedMaterialDependency: ['text_first', 'photo_optional', 'ui_capture_required'],
    supportedInteractions: ['static', 'light', 'mobile_conversion_focused'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '特徴一覧', {
        minItems: 4,
        maxItems: 8,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 4,
      maxItems: 8,
      maxHeadingLength: 28,
      maxBodyLength: 56,
      toneNotes: [
        '一覧性を優先し、1項目ごとに違う価値が見えるようにする',
        '詳細説明はfeaturesやuse_casesに寄せ、ここでは把握しやすさを優先する',
        'BtoB資料請求では、初見でも比較しやすい判断軸になる言葉を選ぶ',
      ],
      prohibitedPatterns: ['長文カード化', '同じ意味の機能を並べる'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 5, 3, 4, 4, 4),
  },
  {
    id: 'reasons_icon_list',
    label: 'Reasons Icon List',
    description: '選ばれる理由を短く並べる reasons section。',
    version: 1,
    status: 'active',
    priority: 92,
    tags: ['reasons'],
    sectionType: 'reasons',
    layoutType: 'icon_list',
    supportedFamilies: ['regional_service_inquiry', 'btob_inquiry', 'education_application'],
    supportedAppealStyles: ['trust_relief', 'problem_solution', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'friendly', 'natural_warm', 'corporate'],
    supportedMaterialDependency: ['text_first', 'photo_optional'],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '理由一覧', {
        minItems: 3,
        maxItems: 5,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 5,
      maxHeadingLength: 28,
      maxBodyLength: 52,
      toneNotes: ['短く明快にする'],
      prohibitedPatterns: ['proof の内容をそのまま転記する'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 4, 2, 3, 3, 2),
  },
  {
    id: 'reasons_short_bullets',
    label: 'Reasons Short Bullets',
    description: '短い箇条書きで理由を見せる軽量 reasons section。',
    version: 1,
    status: 'active',
    priority: 86,
    tags: ['reasons'],
    sectionType: 'reasons',
    layoutType: 'short_bullets',
    supportedFamilies: ['regional_service_inquiry', 'store_reservation'],
    supportedAppealStyles: ['trust_relief', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light'],
    supportedTones: ['friendly', 'natural_warm', 'trust'],
    supportedMaterialDependency: ['text_first', 'photo_optional'],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '理由一覧', {
        minItems: 3,
        maxItems: 4,
        itemShape: { title: 'text', body: 'text' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 4,
      maxHeadingLength: 24,
      maxBodyLength: 36,
      toneNotes: ['予約前に知りたい要点だけに絞る'],
      prohibitedPatterns: ['長文の説明'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(4, 3, 3, 4, 2, 2),
  },
  {
    id: 'benefit_cards',
    label: 'Benefit Cards',
    description: '導入後や購入後の便益をカードで伝える benefit section。',
    version: 1,
    status: 'active',
    priority: 90,
    tags: ['benefit'],
    sectionType: 'benefit',
    layoutType: 'benefit_cards',
    supportedFamilies: ['d2c_purchase', 'education_application', 'seminar_application'],
    supportedAppealStyles: ['benefit_future', 'story_empathy'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['friendly', 'premium', 'bright_clean', 'trust'],
    supportedMaterialDependency: ['text_first', 'photo_optional'],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '便益一覧', {
        minItems: 3,
        maxItems: 4,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 4,
      maxHeadingLength: 28,
      maxBodyLength: 72,
      toneNotes: ['未来の状態を具体的にする'],
      prohibitedPatterns: ['features と同じ機能説明'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 5, 2, 2, 3, 2),
  },
  {
    id: 'benefit_bullets',
    label: 'Benefit Bullets',
    description: '短い bullets で便益を見せる benefit section。',
    version: 1,
    status: 'active',
    priority: 84,
    tags: ['benefit'],
    sectionType: 'benefit',
    layoutType: 'benefit_bullets',
    supportedFamilies: ['d2c_purchase', 'store_reservation'],
    supportedAppealStyles: ['benefit_future', 'offer_push'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light'],
    supportedTones: ['friendly', 'bright_clean', 'premium'],
    supportedMaterialDependency: ['text_first', 'photo_optional'],
    supportedInteractions: ['static', 'mobile_conversion_focused'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '便益一覧', {
        minItems: 3,
        maxItems: 5,
        itemShape: { title: 'text', body: 'text' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 5,
      maxHeadingLength: 24,
      maxBodyLength: 32,
      toneNotes: ['一読で価値が伝わる短さを優先する'],
      prohibitedPatterns: ['説明過多'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(4, 4, 3, 4, 2, 2),
  },
  {
    id: 'benefit_future_visual',
    label: 'Benefit Future Visual',
    description: '利用後のイメージを大きく見せる benefit section。',
    version: 1,
    status: 'active',
    priority: 93,
    tags: ['benefit', 'visual'],
    sectionType: 'benefit',
    layoutType: 'future_visual',
    supportedFamilies: ['d2c_purchase', 'store_reservation'],
    supportedAppealStyles: ['benefit_future', 'story_empathy'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['premium', 'bright_clean', 'friendly'],
    supportedMaterialDependency: ['photo_optional', 'photo_required'],
    supportedInteractions: ['static', 'light', 'mobile_conversion_focused'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '便益一覧', {
        minItems: 2,
        maxItems: 3,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [imageField('image', 'ベネフィットを想起させる写真', { sourceKinds: ['service_photo'] })],
    assetRequirements: [],
    contentRules: {
      minItems: 2,
      maxItems: 3,
      maxHeadingLength: 26,
      maxBodyLength: 64,
      toneNotes: ['感情だけでなく利用価値に着地させる'],
      prohibitedPatterns: ['誇張しすぎる before/after'],
    },
    emptyStateRules: {
      policy: 'omit_optional_fields',
      omitFieldNames: ['image'],
    },
    rankingSignals: rankingSignals(5, 5, 3, 3, 5, 5),
  },
  {
    id: 'proof_metric_cards',
    label: 'Proof Metric Cards',
    description: '導入社数・満足度・改善実績などを一目で信頼材料として理解させる proof section。',
    version: 1,
    status: 'active',
    priority: 98,
    tags: ['proof', 'metrics'],
    sectionType: 'proof',
    layoutType: 'metric_cards',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry', 'education_application', 'recruitment_entry', 'seminar_application', 'regional_service_inquiry'],
    supportedAppealStyles: ['trust_relief', 'authority_expert', 'problem_solution'],
    supportedDevices: ['balanced', 'desktop_first', 'mobile_first'],
    supportedInformationDensity: ['light', 'standard', 'rich'],
    supportedTones: ['trust', 'corporate', 'modern'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'photo_required',
      'ui_capture_required',
      'person_photo_required',
    ],
    supportedInteractions: ['static', 'light', 'guided', 'mobile_conversion_focused'],
    requiredFields: [
      listField('items', 'metric_list', '数字実績', {
        allowAiGeneration: false,
        requiresVerifiedSource: true,
        minItems: 3,
        maxItems: 5,
        sourceKinds: ['proof_metric', 'proof_case', 'certification'],
        itemShape: { value: 'text', label: 'text' },
      }),
    ],
    optionalFields: [textField('heading', '見出し', { required: false })],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 5,
      maxHeadingLength: 28,
      toneNotes: [
        '数字の意味が一目で分かるように母数や文脈を簡潔に補う',
        'HeroやCTAの直前で安心材料として効く並びにする',
        'BtoB資料請求では、資料を受け取る理由になる数字を優先して置く',
      ],
      prohibitedPatterns: ['推測値の記載', '単位や条件が曖昧な数字を置く'],
    },
    emptyStateRules: {
      policy: 'omit_section',
    },
    rankingSignals: rankingSignals(5, 4, 3, 3, 3, 3),
  },
  {
    id: 'proof_badges_awards',
    label: 'Proof Badges Awards',
    description: '資格・受賞・導入実績などをバッジ状に並べて短く信頼を積む proof section。',
    version: 1,
    status: 'active',
    priority: 93,
    tags: ['proof', 'badges'],
    sectionType: 'proof',
    layoutType: 'badges_awards',
    supportedFamilies: ['regional_service_inquiry', 'education_application', 'seminar_application', 'btob_inquiry'],
    supportedAppealStyles: ['trust_relief', 'authority_expert'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard', 'rich'],
    supportedTones: ['trust', 'natural_warm', 'corporate'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'photo_required',
      'ui_capture_required',
      'person_photo_required',
    ],
    supportedInteractions: ['static', 'light', 'guided', 'mobile_conversion_focused'],
    requiredFields: [
      listField('items', 'item_list', '資格・受賞・導入実績', {
        allowAiGeneration: false,
        requiresVerifiedSource: true,
        minItems: 2,
        maxItems: 5,
        sourceKinds: ['certification', 'proof_case'],
        itemShape: { title: 'text', body: 'text' },
      }),
    ],
    optionalFields: [textField('heading', '見出し', { required: false })],
    assetRequirements: [],
    contentRules: {
      minItems: 2,
      maxItems: 5,
      maxHeadingLength: 28,
      toneNotes: [
        '資格や実績の読み替えがしやすい短い表現にする',
        '読み手が安心材料として拾いやすい順に並べる',
        'BtoB資料請求では、補助的な信頼材料として主張を増やしすぎない',
      ],
      prohibitedPatterns: ['存在しない認証の記載', '根拠の弱い肩書きを権威として見せる'],
    },
    emptyStateRules: {
      policy: 'omit_section',
    },
    rankingSignals: rankingSignals(4, 4, 2, 2, 3, 3),
  },
  {
    id: 'review_speech_bubbles',
    label: 'Review Speech Bubbles',
    description: '同じ悩みを持つ人の声を短く拾わせる、共感重視の review section。',
    version: 1,
    status: 'active',
    priority: 88,
    tags: ['review'],
    sectionType: 'review',
    layoutType: 'speech_bubbles',
    supportedFamilies: ['regional_service_inquiry', 'store_reservation', 'recruitment_entry', 'd2c_purchase'],
    supportedAppealStyles: ['story_empathy', 'trust_relief'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['friendly', 'natural_warm', 'bright_clean', 'trust'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'photo_required',
      'person_photo_required',
    ],
    supportedInteractions: ['static', 'light', 'guided', 'mobile_conversion_focused'],
    requiredFields: [
      listField('items', 'person_list', 'レビュー一覧', {
        allowAiGeneration: false,
        requiresVerifiedSource: true,
        minItems: 2,
        maxItems: 4,
        sourceKinds: ['review'],
        itemShape: { name: 'text', role: 'text', comment: 'textarea' },
      }),
    ],
    optionalFields: [textField('heading', '見出し', { required: false })],
    assetRequirements: [],
    contentRules: {
      minItems: 2,
      maxItems: 4,
      maxHeadingLength: 26,
      toneNotes: [
        '一人ひとりの悩みと変化が短く鮮明に伝わるようにする',
        '同じ読者層が自分ごと化しやすい属性や利用文脈を残す',
      ],
      prohibitedPatterns: ['AI でレビューを創作する', '褒め言葉だけで具体性がないレビューにする'],
    },
    emptyStateRules: {
      policy: 'omit_section',
    },
    rankingSignals: rankingSignals(4, 4, 3, 3, 2, 2),
  },
  {
    id: 'review_case_reinforcement',
    label: 'Review Case Reinforcement',
    description: '導入背景・変化・評価理由を短い事例カードとして見せ、BtoBの比較検討を後押しする review section。',
    version: 1,
    status: 'active',
    priority: 95,
    tags: ['review', 'b2b', 'case'],
    sectionType: 'review',
    layoutType: 'case_highlights',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry', 'seminar_application', 'education_application'],
    supportedAppealStyles: ['trust_relief', 'problem_solution', 'authority_expert'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['standard', 'rich'],
    supportedTones: ['trust', 'corporate', 'modern'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'photo_required',
      'ui_capture_required',
      'person_photo_required',
    ],
    supportedInteractions: ['static', 'light', 'guided'],
    requiredFields: [
      listField('items', 'person_list', '導入事例コメント', {
        allowAiGeneration: false,
        requiresVerifiedSource: true,
        minItems: 2,
        maxItems: 3,
        sourceKinds: ['review', 'proof_case'],
        itemShape: { name: 'text', role: 'text', comment: 'textarea' },
      }),
    ],
    optionalFields: [textField('heading', '見出し', { required: false })],
    assetRequirements: [],
    contentRules: {
      minItems: 2,
      maxItems: 3,
      maxHeadingLength: 28,
      toneNotes: [
        '会社名や役割が分かる範囲で残し、比較検討に使える事例として見せる',
        '導入前の悩み、利用後の変化、評価理由のどれかが必ず伝わるコメントにする',
        'BtoB資料請求では、誇張ではなく判断材料になる具体性を優先する',
      ],
      prohibitedPatterns: ['抽象的な賛辞だけで終わる', '成果が見えない長文コメントをそのまま置く'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'review_speech_bubbles',
    },
    rankingSignals: rankingSignals(5, 4, 2, 3, 3, 3),
  },
  {
    id: 'review_with_photo',
    label: 'Review With Photo',
    description: '写真や属性とセットで実在感を高め、利用後の変化を具体化する review section。',
    version: 1,
    status: 'active',
    priority: 92,
    tags: ['review', 'photo'],
    sectionType: 'review',
    layoutType: 'photo_reviews',
    supportedFamilies: ['store_reservation', 'd2c_purchase', 'regional_service_inquiry'],
    supportedAppealStyles: ['story_empathy', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['friendly', 'natural_warm', 'premium', 'bright_clean'],
    supportedMaterialDependency: ['photo_optional', 'person_photo_required'],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      listField('items', 'person_list', 'レビュー一覧', {
        allowAiGeneration: false,
        requiresVerifiedSource: true,
        minItems: 2,
        maxItems: 3,
        sourceKinds: ['review'],
        itemShape: { name: 'text', role: 'text', comment: 'textarea', image: 'image' },
      }),
    ],
    optionalFields: [textField('heading', '見出し', { required: false })],
    assetRequirements: [
      {
        assetKind: 'person_photo',
        required: false,
        purpose: 'レビューの実在感を補強する',
      },
    ],
    contentRules: {
      minItems: 2,
      maxItems: 3,
      maxHeadingLength: 26,
      toneNotes: [
        '写真・属性・コメントの3点で実在感を作る',
        '写真が弱いときは speech bubble に落とし、内容の具体性を優先する',
      ],
      prohibitedPatterns: ['写真だけで信頼を補おうとする', '写真とコメントの文脈がずれる'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'review_speech_bubbles',
    },
    rankingSignals: rankingSignals(5, 4, 2, 2, 3, 4),
  },
  {
    id: 'review_carousel',
    label: 'Review Carousel',
    description: 'スマホで短く読み進められるよう、ベネフィットが伝わる声を軽く並べる review。',
    version: 1,
    status: 'active',
    priority: 82,
    tags: ['review', 'carousel'],
    sectionType: 'review',
    layoutType: 'carousel',
    supportedFamilies: ['store_reservation', 'd2c_purchase'],
    supportedAppealStyles: ['story_empathy', 'benefit_future'],
    supportedDevices: ['mobile_first', 'balanced'],
    supportedInformationDensity: ['light'],
    supportedTones: ['bright_clean', 'friendly', 'premium'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'photo_required',
      'ui_capture_required',
      'person_photo_required',
    ],
    supportedInteractions: ['light', 'mobile_conversion_focused'],
    requiredFields: [
      listField('items', 'person_list', 'レビュー一覧', {
        allowAiGeneration: false,
        requiresVerifiedSource: true,
        minItems: 2,
        maxItems: 5,
        sourceKinds: ['review'],
        itemShape: { name: 'text', role: 'text', comment: 'textarea' },
      }),
    ],
    optionalFields: [textField('heading', '見出し', { required: false })],
    assetRequirements: [],
    contentRules: {
      minItems: 2,
      maxItems: 5,
      maxHeadingLength: 24,
      toneNotes: [
        '短いスワイプ体験に向くように1枚ごとの主張を一つに絞る',
        '読むたびに違うベネフィットが見える順に並べる',
      ],
      prohibitedPatterns: ['長文レビューを無理に詰める', '同じ内容のレビューを並べる'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'review_speech_bubbles',
    },
    rankingSignals: rankingSignals(4, 3, 5, 4, 2, 2),
  },
  {
    id: 'faq_accordion',
    label: 'FAQ Accordion',
    description: '標準的な FAQ accordion。',
    version: 1,
    status: 'active',
    priority: 95,
    tags: ['faq'],
    sectionType: 'faq',
    layoutType: 'accordion',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry', 'regional_service_inquiry', 'store_reservation', 'd2c_purchase', 'seminar_application', 'education_application', 'recruitment_entry'],
    supportedAppealStyles: ['trust_relief', 'problem_solution', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: [...ALL_DENSITIES],
    supportedTones: ['trust', 'corporate', 'modern', 'friendly', 'premium', 'natural_warm', 'bright_clean'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS],
    supportedInteractions: ['light', 'guided'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'qa_list', 'FAQ 一覧', {
        minItems: 3,
        maxItems: 6,
        itemShape: { question: 'text', answer: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 6,
      maxHeadingLength: 24,
      maxBodyLength: 120,
      toneNotes: ['購買や申込の阻害要因を先に潰す'],
      prohibitedPatterns: ['同じ質問の言い換え'],
    },
    emptyStateRules: {
      policy: 'omit_section',
    },
    rankingSignals: rankingSignals(5, 3, 4, 4, 3, 2),
  },
  {
    id: 'faq_two_columns',
    label: 'FAQ Two Columns',
    description: '比較的情報量の多い FAQ 向けの 2 カラム layout。',
    version: 1,
    status: 'active',
    priority: 88,
    tags: ['faq'],
    sectionType: 'faq',
    layoutType: 'two_columns',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry'],
    supportedAppealStyles: ['trust_relief', 'feature_understanding'],
    supportedDevices: ['balanced', 'desktop_first'],
    supportedInformationDensity: ['standard', 'rich'],
    supportedTones: ['corporate', 'modern', 'trust'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS],
    supportedInteractions: ['light'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'qa_list', 'FAQ 一覧', {
        minItems: 4,
        maxItems: 8,
        itemShape: { question: 'text', answer: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 4,
      maxItems: 8,
      maxHeadingLength: 24,
      maxBodyLength: 110,
      toneNotes: ['比較検討でよく出る質問を網羅する'],
      prohibitedPatterns: ['モバイルで読みにくい長文'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'faq_accordion',
    },
    rankingSignals: rankingSignals(4, 4, 4, 5, 4, 2),
  },
  {
    id: 'faq_cards',
    label: 'FAQ Cards',
    description: '軽やかな card で FAQ を見せる layout。',
    version: 1,
    status: 'active',
    priority: 84,
    tags: ['faq'],
    sectionType: 'faq',
    layoutType: 'faq_cards',
    supportedFamilies: ['store_reservation', 'd2c_purchase', 'regional_service_inquiry'],
    supportedAppealStyles: ['benefit_future', 'trust_relief'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['friendly', 'premium', 'bright_clean', 'natural_warm'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS],
    supportedInteractions: ['light'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'qa_list', 'FAQ 一覧', {
        minItems: 3,
        maxItems: 5,
        itemShape: { question: 'text', answer: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 5,
      maxHeadingLength: 24,
      maxBodyLength: 96,
      toneNotes: ['予約・購入・問い合わせを止める不安を短く返す'],
      prohibitedPatterns: ['説明過多'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'faq_accordion',
    },
    rankingSignals: rankingSignals(4, 3, 3, 3, 3, 2),
  },
  {
    id: 'faq_b2b_reassurance_panels',
    label: 'FAQ B2B Reassurance Panels',
    description: '比較検討で出やすい不安を、判断しやすい短いパネルで返す BtoB 向け FAQ。',
    version: 1,
    status: 'active',
    priority: 96,
    tags: ['faq', 'b2b'],
    sectionType: 'faq',
    layoutType: 'reassurance_panels',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry'],
    supportedAppealStyles: ['trust_relief', 'problem_solution', 'authority_expert'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['standard', 'rich'],
    supportedTones: ['trust', 'corporate', 'modern'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS],
    supportedInteractions: ['light', 'guided'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'qa_list', 'FAQ 一覧', {
        minItems: 3,
        maxItems: 6,
        itemShape: { question: 'text', answer: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 6,
      maxHeadingLength: 26,
      maxBodyLength: 110,
      toneNotes: [
        '資料請求前に出やすい不安を、比較検討しやすい言葉で返す',
        '営業色が強すぎない安心感と、送信後の見通しの両方を含める',
      ],
      prohibitedPatterns: ['質問に対して答えが抽象的すぎる', '押しの強い売り込みで締める'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'faq_accordion',
    },
    rankingSignals: rankingSignals(5, 4, 4, 4, 4, 3),
  },
  {
    id: 'comparison_table',
    label: 'Comparison Table',
    description: '比較検討に使いやすい comparison table。',
    version: 1,
    status: 'active',
    priority: 93,
    tags: ['comparison'],
    sectionType: 'comparison',
    layoutType: 'table',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry'],
    supportedAppealStyles: ['feature_understanding', 'problem_solution'],
    supportedDevices: ['balanced', 'desktop_first'],
    supportedInformationDensity: ['standard', 'rich'],
    supportedTones: ['corporate', 'modern', 'trust'],
    supportedMaterialDependency: ['text_first', 'photo_optional', 'ui_capture_required'],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '比較項目', {
        minItems: 3,
        maxItems: 6,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 6,
      maxHeadingLength: 28,
      maxBodyLength: 72,
      toneNotes: [
        '競合比較でなくても比較軸が分かる構成にする',
        '社内共有しやすい比較軸を優先し、判断のしやすさを重視する',
      ],
      prohibitedPatterns: ['証拠のない優位性を断定する'],
    },
    emptyStateRules: {
      policy: 'omit_section',
    },
    rankingSignals: rankingSignals(5, 5, 5, 4, 4, 4),
  },
  {
    id: 'comparison_checklist',
    label: 'Comparison Checklist',
    description: '比較軸を checklist 風に見せる軽量 comparison。',
    version: 1,
    status: 'active',
    priority: 82,
    tags: ['comparison'],
    sectionType: 'comparison',
    layoutType: 'checklist',
    supportedFamilies: ['btob_inquiry', 'education_application'],
    supportedAppealStyles: ['feature_understanding', 'trust_relief'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'corporate'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'photo_required',
      'ui_capture_required',
      'person_photo_required',
    ],
    supportedInteractions: ['static', 'light', 'guided', 'mobile_conversion_focused'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '比較項目', {
        minItems: 3,
        maxItems: 5,
        itemShape: { title: 'text', body: 'text' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 5,
      maxHeadingLength: 24,
      maxBodyLength: 40,
      toneNotes: ['比較軸を軽く見せる'],
      prohibitedPatterns: ['table が必要な情報量を無理に checklist 化する'],
    },
    emptyStateRules: {
      policy: 'omit_section',
    },
    rankingSignals: rankingSignals(3, 4, 2, 2, 2, 2),
  },
  {
    id: 'use_cases_industry_examples',
    label: 'Use Cases Industry Examples',
    description: '業種や利用シーン別の use case を見せる section。',
    version: 1,
    status: 'active',
    priority: 88,
    tags: ['use-cases'],
    sectionType: 'use_cases',
    layoutType: 'industry_examples',
    supportedFamilies: ['saas_ai_inquiry', 'btob_inquiry'],
    supportedAppealStyles: ['feature_understanding', 'problem_solution'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['standard', 'rich'],
    supportedTones: ['modern', 'corporate', 'trust'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'photo_required',
      'ui_capture_required',
      'person_photo_required',
    ],
    supportedInteractions: ['static', 'light', 'guided', 'mobile_conversion_focused'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '利用例', {
        minItems: 3,
        maxItems: 4,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 4,
      maxHeadingLength: 28,
      maxBodyLength: 84,
      toneNotes: ['誰がどう使うかを具体化する'],
      prohibitedPatterns: ['features を言い換えるだけにしない'],
    },
    emptyStateRules: {
      policy: 'omit_section',
    },
    rankingSignals: rankingSignals(4, 4, 2, 3, 3, 2),
  },
  {
    id: 'pricing_compact_cards',
    label: 'Pricing Compact Cards',
    description: '料金やプラン差分を短時間で判断できるように整理する pricing section。',
    version: 1,
    status: 'active',
    priority: 90,
    tags: ['pricing'],
    sectionType: 'pricing',
    layoutType: 'compact_cards',
    supportedFamilies: ['store_reservation', 'd2c_purchase', 'education_application'],
    supportedAppealStyles: ['benefit_future', 'offer_push', 'trust_relief'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['friendly', 'premium', 'bright_clean', 'trust'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'photo_required',
      'ui_capture_required',
      'person_photo_required',
    ],
    supportedInteractions: ['static', 'light', 'mobile_conversion_focused'],
    requiredFields: [
      listField('items', 'price_list', '価格一覧', {
        allowAiGeneration: false,
        requiresVerifiedSource: true,
        minItems: 1,
        maxItems: 4,
        sourceKinds: ['pricing'],
        itemShape: { title: 'text', price: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [textField('heading', '見出し', { required: false })],
    assetRequirements: [],
    contentRules: {
      minItems: 1,
      maxItems: 4,
      maxHeadingLength: 24,
      toneNotes: [
        '価格だけでなく、何が含まれるかと差分が分かるようにする',
        '検討初期でも判断しやすい言い方を優先する',
      ],
      prohibitedPatterns: ['価格の捏造', '含まれる内容が分からない価格表現'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'pricing_consultation',
    },
    rankingSignals: rankingSignals(5, 4, 3, 2, 3, 2),
  },
  {
    id: 'pricing_color_cards',
    label: 'Pricing Color Cards',
    description: 'プランごとに warm/cool/soft の色を切り替える、地域サービスや一般消費者向けの pricing。',
    version: 1,
    status: 'active',
    priority: 84,
    tags: ['pricing', 'colorful'],
    sectionType: 'pricing',
    layoutType: 'numbered_steps',
    supportedFamilies: ['regional_service_inquiry', 'store_reservation', 'd2c_purchase'],
    supportedAppealStyles: ['offer_push', 'benefit_future', 'trust_relief'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'friendly', 'bright_clean'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '料金プラン', {
        minItems: 2,
        maxItems: 4,
        itemShape: { title: 'text', price: 'text', body: 'text' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 2,
      maxItems: 4,
      maxHeadingLength: 20,
      maxBodyLength: 60,
      toneNotes: ['プラン名+価格を大きく、サブ説明は最小限に'],
      prohibitedPatterns: ['すべてのカードに過剰な細則を入れる'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'pricing_consultation',
    },
    rankingSignals: rankingSignals(4, 3, 3, 2, 2, 3),
  },
  {
    id: 'pricing_consultation',
    label: 'Pricing Consultation',
    description: '価格の個別性が高い時に、相談する理由を納得感付きで伝える pricing fallback。',
    version: 1,
    status: 'active',
    priority: 82,
    tags: ['pricing', 'fallback'],
    sectionType: 'pricing',
    layoutType: 'consultation',
    supportedFamilies: ['store_reservation', 'd2c_purchase', 'education_application', 'btob_inquiry'],
    supportedAppealStyles: ['trust_relief', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'friendly', 'premium'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'photo_required',
      'ui_capture_required',
      'person_photo_required',
    ],
    supportedInteractions: ['static', 'light', 'guided', 'mobile_conversion_focused'],
    requiredFields: [
      textField('heading', '見出し'),
      textareaField('body', '価格案内文'),
      textField('primaryCtaLabel', 'CTA 文言'),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      maxHeadingLength: 24,
      maxBodyLength: 84,
      toneNotes: [
        '価格非公開である理由と相談するメリットを簡潔に伝える',
        '不透明さではなく個別最適であることが伝わる文脈にする',
      ],
      prohibitedPatterns: ['価格を匂わせる架空の文言', '問い合わせを強制するだけの文面'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(3, 3, 2, 2, 2, 2),
  },
  {
    id: 'access_simple_text',
    label: 'Access Simple Text',
    description: '住所や最寄り情報を簡潔に見せる access section。',
    version: 1,
    status: 'active',
    priority: 90,
    tags: ['access'],
    sectionType: 'access',
    layoutType: 'simple_text',
    supportedFamilies: ['store_reservation', 'regional_service_inquiry'],
    supportedAppealStyles: ['trust_relief', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'friendly', 'natural_warm', 'bright_clean'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'photo_required',
      'ui_capture_required',
      'person_photo_required',
    ],
    supportedInteractions: ['static', 'light', 'guided', 'mobile_conversion_focused'],
    requiredFields: [
      textField('heading', '見出し'),
      textField('address', '住所', {
        allowAiGeneration: false,
        requiresVerifiedSource: true,
        sourceKinds: ['access'],
      }),
    ],
    optionalFields: [
      textField('accessNote', '最寄り情報', {
        required: false,
        allowAiGeneration: false,
        requiresVerifiedSource: true,
        sourceKinds: ['access'],
      }),
    ],
    assetRequirements: [],
    contentRules: {
      maxHeadingLength: 20,
      maxBodyLength: 64,
      toneNotes: ['予約や来店に必要な情報だけを簡潔に置く'],
      prohibitedPatterns: ['住所の推測記載'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 2, 3, 2, 2, 2),
  },
  {
    id: 'cta_simple_center',
    label: 'CTA Simple Center',
    description: '次の行動と押した後に起こることを安心して理解できる、標準CTA section。',
    version: 1,
    status: 'active',
    priority: 98,
    tags: ['cta'],
    sectionType: 'cta',
    layoutType: 'simple_center',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry', 'regional_service_inquiry', 'store_reservation', 'd2c_purchase', 'seminar_application', 'education_application', 'recruitment_entry'],
    supportedAppealStyles: ['trust_relief', 'problem_solution', 'benefit_future', 'authority_expert', 'people_culture'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: [...ALL_DENSITIES],
    supportedTones: ['trust', 'corporate', 'modern', 'friendly', 'premium', 'natural_warm', 'bright_clean'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS, 'ui_capture_required', 'photo_required', 'person_photo_required'],
    supportedInteractions: [...ALL_INTERACTIONS],
    requiredFields: [
      textField('heading', '見出し'),
      textareaField('body', 'CTA 説明'),
      textField('primaryCtaLabel', '主 CTA 文言'),
    ],
    optionalFields: [textField('secondaryCtaLabel', '副 CTA 文言', { required: false })],
    assetRequirements: [],
    contentRules: {
      maxHeadingLength: 26,
      maxBodyLength: 84,
      toneNotes: [
        '次の行動が明確に分かり、押した後に何が起こるかも想像できる文言にする',
        '日本のBtoB/申込LPのように安心して押せる温度感を保つ',
      ],
      prohibitedPatterns: ['過剰な煽り', '行動理由がないままCTAだけを置く'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 4, 3, 3, 4, 3),
  },
  {
    id: 'cta_b2b_reassurance_panel',
    label: 'CTA B2B Reassurance Panel',
    description: '資料請求前の安心感と、送信後に得られることを整えて見せる BtoB 向け CTA。',
    version: 1,
    status: 'active',
    priority: 97,
    tags: ['cta', 'b2b'],
    sectionType: 'cta',
    layoutType: 'reassurance_panel',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry'],
    supportedAppealStyles: ['trust_relief', 'problem_solution', 'authority_expert'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'corporate', 'modern'],
    supportedMaterialDependency: ['text_first', 'photo_optional', 'ui_capture_required'],
    supportedInteractions: ['light', 'guided'],
    requiredFields: [
      textField('heading', '見出し'),
      textareaField('body', 'CTA 説明'),
      textField('primaryCtaLabel', '主 CTA 文言'),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      maxHeadingLength: 28,
      maxBodyLength: 72,
      toneNotes: [
        '資料で分かることと送信後の流れが短く伝わるようにする',
        '営業色を上げすぎず、比較検討や社内共有の文脈に寄せる',
      ],
      prohibitedPatterns: ['強すぎる煽り', '送信後の見通しがないままCTAだけ置く'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 4, 3, 3, 4, 4),
  },
  {
    id: 'cta_banner_emphasis',
    label: 'CTA Banner Emphasis',
    description: '限定性や今動く理由を一つに絞って強く後押しする banner CTA。',
    version: 1,
    status: 'active',
    priority: 84,
    tags: ['cta', 'banner'],
    sectionType: 'cta',
    layoutType: 'banner_emphasis',
    supportedFamilies: ['store_reservation', 'd2c_purchase', 'seminar_application'],
    supportedAppealStyles: ['offer_push', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['premium', 'bright_clean', 'friendly'],
    supportedMaterialDependency: ['text_first', 'photo_optional', 'photo_required'],
    supportedInteractions: ['light', 'mobile_conversion_focused'],
    requiredFields: [
      textField('heading', '見出し'),
      textareaField('body', 'CTA 説明'),
      textField('primaryCtaLabel', '主 CTA 文言'),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      maxHeadingLength: 24,
      maxBodyLength: 64,
      toneNotes: [
        '限定性や今動く理由を一つに絞って強く見せる',
        '押しが強くても下品に見えない短さと明快さを優先する',
      ],
      prohibitedPatterns: ['安っぽい煽り文句', '根拠のない緊急性を足す'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 5, 3, 2, 3, 2),
  },
  {
    id: 'cta_sticky_mobile',
    label: 'CTA Sticky Mobile',
    description: 'モバイル下部固定の CTA。',
    version: 1,
    status: 'active',
    priority: 80,
    tags: ['cta', 'mobile'],
    sectionType: 'cta',
    layoutType: 'sticky_mobile',
    supportedFamilies: ['store_reservation', 'd2c_purchase', 'seminar_application', 'education_application'],
    supportedAppealStyles: ['offer_push', 'benefit_future', 'trust_relief'],
    supportedDevices: ['mobile_first'],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['friendly', 'bright_clean', 'premium', 'trust'],
    supportedMaterialDependency: ['text_first', 'photo_optional'],
    supportedInteractions: ['mobile_conversion_focused'],
    requiredFields: [textField('primaryCtaLabel', '主 CTA 文言')],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      maxHeadingLength: 16,
      toneNotes: ['短く押しやすくする'],
      prohibitedPatterns: ['複数 CTA の併記'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 4, 5, 3, 2, 2),
  },
  {
    id: 'form_standard_contact',
    label: 'Form Standard Contact',
    description: '標準的な問い合わせ / 申込 form section。',
    version: 1,
    status: 'active',
    priority: 96,
    tags: ['form'],
    sectionType: 'form',
    layoutType: 'standard_contact',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry', 'regional_service_inquiry', 'seminar_application', 'education_application', 'recruitment_entry'],
    supportedAppealStyles: ['trust_relief', 'problem_solution', 'authority_expert', 'people_culture'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard', 'rich'],
    supportedTones: ['trust', 'corporate', 'modern', 'natural_warm'],
    supportedMaterialDependency: [
      ...TEXT_FRIENDLY_MATERIALS,
      'photo_required',
      'ui_capture_required',
      'person_photo_required',
    ],
    supportedInteractions: ['light', 'guided', 'mobile_conversion_focused'],
    requiredFields: [
      textField('heading', '見出し'),
      textareaField('body', '補足説明'),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      maxHeadingLength: 24,
      maxBodyLength: 96,
      toneNotes: [
        '入力前の不安を減らす説明を添える',
        '資料請求では送信後に何が届くかまで想像できる文脈にする',
      ],
      prohibitedPatterns: ['入力項目の推測定義', '送信後の流れが見えないまま終える'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 3, 2, 2, 3, 2),
  },
  {
    id: 'overview_cards',
    label: 'Overview Cards',
    description: 'イベントや講座の概要を整理して見せる section。',
    version: 1,
    status: 'active',
    priority: 90,
    tags: ['overview'],
    sectionType: 'overview',
    layoutType: 'overview_cards',
    supportedFamilies: ['seminar_application'],
    supportedAppealStyles: ['authority_expert', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'modern'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS, 'person_photo_required'],
    supportedInteractions: ['static', 'light', 'guided'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '概要項目', {
        minItems: 3,
        maxItems: 5,
        itemShape: { title: 'text', body: 'text' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 5,
      maxHeadingLength: 24,
      maxBodyLength: 48,
      toneNotes: ['開催概要を一目で理解できる粒度にする'],
      prohibitedPatterns: ['speaker section と内容を重複させる'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 4, 2, 2, 3, 2),
  },
  {
    id: 'speaker_profile_cards',
    label: 'Speaker Profile Cards',
    description: '登壇者情報を整理して見せる section。',
    version: 1,
    status: 'active',
    priority: 88,
    tags: ['speaker'],
    sectionType: 'speaker',
    layoutType: 'profile_cards',
    supportedFamilies: ['seminar_application'],
    supportedAppealStyles: ['authority_expert', 'trust_relief'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'modern'],
    supportedMaterialDependency: ['text_first', 'photo_optional', 'person_photo_required'],
    supportedInteractions: ['static', 'light', 'guided'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'person_list', '登壇者一覧', {
        minItems: 1,
        maxItems: 3,
        itemShape: { name: 'text', role: 'text', comment: 'textarea', image: 'image' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 1,
      maxItems: 3,
      maxHeadingLength: 24,
      maxBodyLength: 84,
      toneNotes: ['実績と今回話す価値を結びつける'],
      prohibitedPatterns: ['経歴の羅列だけで終わる'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 5, 2, 2, 3, 3),
  },
  {
    id: 'instructor_profile_cards',
    label: 'Instructor Profile Cards',
    description: '講師紹介のための section。',
    version: 1,
    status: 'active',
    priority: 88,
    tags: ['instructor'],
    sectionType: 'instructor',
    layoutType: 'profile_cards',
    supportedFamilies: ['education_application'],
    supportedAppealStyles: ['authority_expert', 'trust_relief'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard', 'rich'],
    supportedTones: ['trust', 'natural_warm'],
    supportedMaterialDependency: ['text_first', 'photo_optional', 'person_photo_required'],
    supportedInteractions: ['static', 'light', 'guided'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'person_list', '講師一覧', {
        minItems: 1,
        maxItems: 3,
        itemShape: { name: 'text', role: 'text', comment: 'textarea', image: 'image' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 1,
      maxItems: 3,
      maxHeadingLength: 24,
      maxBodyLength: 84,
      toneNotes: ['講師の強みと指導価値を結びつける'],
      prohibitedPatterns: ['資格や実績の裏付けがない強い断定'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 5, 2, 2, 3, 3),
  },
  {
    id: 'work_style_benefit_cards',
    label: 'Work Style Benefit Cards',
    description: '働き方や制度の魅力を見せる section。',
    version: 1,
    status: 'active',
    priority: 88,
    tags: ['work-style'],
    sectionType: 'work_style',
    layoutType: 'benefit_cards',
    supportedFamilies: ['recruitment_entry'],
    supportedAppealStyles: ['people_culture', 'story_empathy'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['natural_warm', 'trust', 'friendly'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS, 'person_photo_required'],
    supportedInteractions: ['static', 'light', 'guided'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '働き方項目', {
        minItems: 3,
        maxItems: 5,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 5,
      maxHeadingLength: 26,
      maxBodyLength: 72,
      toneNotes: ['制度ではなく働く体験として伝える'],
      prohibitedPatterns: ['people section と同じ内容を並べる'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 5, 2, 2, 2, 2),
  },
  {
    id: 'course_curriculum_outline',
    label: 'Course Curriculum Outline',
    description: '講座内容やカリキュラムを一覧化する section。',
    version: 1,
    status: 'active',
    priority: 90,
    tags: ['curriculum'],
    sectionType: 'course_or_curriculum',
    layoutType: 'curriculum_outline',
    supportedFamilies: ['education_application'],
    supportedAppealStyles: ['benefit_future', 'trust_relief', 'authority_expert'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['standard', 'rich'],
    supportedTones: ['trust', 'natural_warm'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS, 'person_photo_required'],
    supportedInteractions: ['static', 'light', 'guided'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', 'カリキュラム項目', {
        minItems: 3,
        maxItems: 8,
        itemShape: { title: 'text', body: 'textarea' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 8,
      maxHeadingLength: 28,
      maxBodyLength: 84,
      toneNotes: ['学ぶ順序と得られる成果が分かるようにする'],
      prohibitedPatterns: ['抽象的な学習項目だけを並べる'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 4, 2, 4, 3, 2),
  },
  {
    id: 'offer_limited_banner',
    label: 'Offer Limited Banner',
    description: '限定性や特典を見せる offer section。',
    version: 1,
    status: 'active',
    priority: 86,
    tags: ['offer'],
    sectionType: 'offer',
    layoutType: 'limited_banner',
    supportedFamilies: ['store_reservation', 'd2c_purchase', 'seminar_application'],
    supportedAppealStyles: ['offer_push', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['bright_clean', 'premium', 'friendly'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS],
    supportedInteractions: ['light', 'mobile_conversion_focused'],
    requiredFields: [
      textField('heading', '見出し'),
      textareaField('body', '特典説明', {
        allowAiGeneration: false,
        requiresVerifiedSource: true,
        sourceKinds: ['offer'],
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      maxHeadingLength: 20,
      maxBodyLength: 64,
      toneNotes: ['条件や期限を曖昧にしない'],
      prohibitedPatterns: ['存在しない限定性の創作'],
    },
    emptyStateRules: {
      policy: 'omit_section',
    },
    rankingSignals: rankingSignals(5, 5, 3, 2, 3, 2),
  },
  {
    id: 'people_profile_cards',
    label: 'People Profile Cards',
    description: '社員やチームを紹介する section。',
    version: 1,
    status: 'active',
    priority: 88,
    tags: ['people'],
    sectionType: 'people',
    layoutType: 'profile_cards',
    supportedFamilies: ['recruitment_entry'],
    supportedAppealStyles: ['people_culture', 'story_empathy'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['natural_warm', 'trust', 'friendly'],
    supportedMaterialDependency: ['text_first', 'photo_optional', 'person_photo_required'],
    supportedInteractions: ['static', 'light', 'guided'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'person_list', '人物一覧', {
        minItems: 2,
        maxItems: 4,
        itemShape: { name: 'text', role: 'text', comment: 'textarea', image: 'image' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 2,
      maxItems: 4,
      maxHeadingLength: 24,
      maxBodyLength: 72,
      toneNotes: ['役割と人柄の両方が見える粒度にする'],
      prohibitedPatterns: ['message section と内容を重複させる'],
    },
    emptyStateRules: {
      policy: 'block_until_user_input',
    },
    rankingSignals: rankingSignals(5, 5, 2, 2, 2, 3),
  },
  {
    id: 'message_text_block',
    label: 'Message Text Block',
    description: '想いや姿勢をまっすぐ伝える message section。',
    version: 1,
    status: 'active',
    priority: 84,
    tags: ['message'],
    sectionType: 'message',
    layoutType: 'text_block',
    supportedFamilies: ['regional_service_inquiry', 'recruitment_entry'],
    supportedAppealStyles: ['story_empathy', 'people_culture', 'trust_relief'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'natural_warm', 'friendly'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('heading', '見出し'),
      textareaField('body', 'メッセージ本文'),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      maxHeadingLength: 24,
      maxBodyLength: 180,
      toneNotes: ['思想や姿勢に絞る'],
      prohibitedPatterns: ['people 紹介の代替にする'],
    },
    emptyStateRules: {
      policy: 'omit_section',
    },
    rankingSignals: rankingSignals(4, 4, 2, 2, 2, 2),
  },
  {
    id: 'message_warning_alert',
    label: 'Message Warning Alert',
    description: '「悪徳業者にご注意」など、ユーザに対する警告・注意喚起を赤系の枠で目立たせる message variant。',
    version: 1,
    status: 'active',
    priority: 86,
    tags: ['message', 'alert', 'regional'],
    sectionType: 'message',
    layoutType: 'text_block',
    supportedFamilies: ['regional_service_inquiry', 'store_reservation'],
    supportedAppealStyles: ['trust_relief'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'friendly'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('heading', '警告タイトル'),
      textareaField('body', '警告本文'),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      maxHeadingLength: 28,
      maxBodyLength: 140,
      toneNotes: ['注意喚起を短く、ユーザ目線で具体的に書く'],
      prohibitedPatterns: ['過度に脅すような表現', '法的根拠なき断定'],
    },
    emptyStateRules: {
      policy: 'omit_section',
    },
    rankingSignals: rankingSignals(3, 4, 2, 2, 2, 3),
  },
  {
    id: 'target_audience_checklist',
    label: 'Target Audience Checklist',
    description: '誰に向いているかを整理する section。',
    version: 1,
    status: 'active',
    priority: 82,
    tags: ['target-audience'],
    sectionType: 'target_audience',
    layoutType: 'checklist',
    supportedFamilies: ['seminar_application', 'education_application'],
    supportedAppealStyles: ['benefit_future', 'trust_relief'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'natural_warm'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', '対象者チェック', {
        minItems: 3,
        maxItems: 5,
        itemShape: { title: 'text', body: 'text' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 5,
      maxHeadingLength: 24,
      maxBodyLength: 40,
      toneNotes: ['対象者を明確にし、対象外も暗に伝える'],
      prohibitedPatterns: ['誰にでも当てはまる表現'],
    },
    emptyStateRules: {
      policy: 'omit_section',
    },
    rankingSignals: rankingSignals(4, 4, 2, 2, 2, 2),
  },
  {
    id: 'flow_standard',
    label: 'Flow Standard',
    description: '利用開始までのステップを順序立てて説明する flow section。',
    version: 1,
    status: 'active',
    priority: 85,
    tags: ['flow', 'generic'],
    sectionType: 'flow',
    layoutType: 'numbered_steps',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry', 'regional_service_inquiry', 'store_reservation', 'd2c_purchase', 'seminar_application', 'education_application', 'recruitment_entry'],
    supportedAppealStyles: ['problem_solution', 'benefit_future', 'trust_relief', 'feature_understanding'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: [...ALL_DENSITIES],
    supportedTones: ['trust', 'corporate', 'friendly', 'modern', 'bright_clean', 'natural_warm'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS],
    supportedInteractions: ['static', 'light', 'guided'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', 'ステップ項目', {
        minItems: 3,
        maxItems: 5,
        itemShape: { title: 'text', body: 'text' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 5,
      maxHeadingLength: 20,
      maxBodyLength: 60,
      toneNotes: ['ステップ番号と短い説明で行動の簡単さを伝える'],
      prohibitedPatterns: ['ステップが多すぎて複雑に見える'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'flow_standard',
    },
    rankingSignals: rankingSignals(2, 2, 2, 2, 2, 2),
  },
  {
    id: 'flow_horizontal_icons',
    label: 'Flow Horizontal Icons',
    description: '4ステップ程度を横並びカードで、矢印とアイコン付きで見せる地域サービス向け flow。',
    version: 1,
    status: 'active',
    priority: 88,
    tags: ['flow', 'horizontal', 'regional'],
    sectionType: 'flow',
    layoutType: 'numbered_steps',
    supportedFamilies: ['regional_service_inquiry', 'store_reservation'],
    supportedAppealStyles: ['trust_relief', 'benefit_future'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'friendly'],
    supportedMaterialDependency: [...TEXT_FRIENDLY_MATERIALS],
    supportedInteractions: ['static', 'light'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'item_list', 'ステップ項目', {
        minItems: 3,
        maxItems: 4,
        itemShape: { title: 'text', body: 'text' },
      }),
    ],
    optionalFields: [],
    assetRequirements: [],
    contentRules: {
      minItems: 3,
      maxItems: 4,
      maxHeadingLength: 20,
      maxBodyLength: 48,
      toneNotes: ['アイコン+矢印で「申込から完了まで」を一目で伝える'],
      prohibitedPatterns: ['ステップが5以上で複雑にする'],
    },
    emptyStateRules: {
      policy: 'fallback_template',
      fallbackTemplateId: 'flow_standard',
    },
    rankingSignals: rankingSignals(3, 3, 2, 2, 2, 3),
  },
  {
    id: 'logo_or_metrics_trust',
    label: 'Logo or Metrics Trust',
    description: '導入ロゴや数字実績を並べて信頼感を出す section。',
    version: 1,
    status: 'active',
    priority: 80,
    tags: ['proof', 'metrics', 'logo'],
    sectionType: 'logo_or_metrics',
    layoutType: 'badge_row',
    supportedFamilies: ['btob_inquiry', 'saas_ai_inquiry', 'regional_service_inquiry'],
    supportedAppealStyles: ['trust_relief', 'authority_expert', 'problem_solution'],
    supportedDevices: [...ALL_DEVICES],
    supportedInformationDensity: ['light', 'standard'],
    supportedTones: ['trust', 'corporate', 'modern', 'premium'],
    supportedMaterialDependency: ['text_first', 'photo_optional'],
    supportedInteractions: ['static'],
    requiredFields: [
      textField('heading', '見出し'),
      listField('items', 'metric_list', '実績数字またはロゴ', {
        minItems: 2,
        maxItems: 6,
        itemShape: { label: 'text', value: 'text' },
      }),
    ],
    optionalFields: [
      listField('logos', 'logo_list', '企業ロゴ', {
        sourceKinds: ['logo'],
        requiresVerifiedSource: true,
      }),
    ],
    assetRequirements: [
      {
        assetKind: 'logo',
        required: false,
        purpose: '導入企業のロゴを掲載して信頼を補強する',
      },
      {
        assetKind: 'metric',
        required: false,
        purpose: '数字実績で信頼感を裏付ける',
      },
    ],
    contentRules: {
      minItems: 2,
      maxItems: 6,
      maxHeadingLength: 20,
      maxBodyLength: 24,
      toneNotes: ['数字は具体的に、ロゴは実在するものだけ使う'],
      prohibitedPatterns: ['実績がないのに数字を捏造する'],
    },
    emptyStateRules: {
      policy: 'omit_section',
    },
    rankingSignals: rankingSignals(3, 3, 1, 2, 2, 4),
  },
]);

export const sectionTemplateRegistryMap = new Map(
  SECTION_TEMPLATE_REGISTRIES.map((template) => [template.id, template] as const),
);

export function getSectionTemplateRegistry(
  templateId: string,
): SectionTemplateRegistry | undefined {
  return sectionTemplateRegistryMap.get(templateId);
}

export function listSectionTemplatesByType(
  sectionType: SectionTemplateRegistry['sectionType'],
): SectionTemplateRegistry[] {
  return SECTION_TEMPLATE_REGISTRIES.filter(
    (template) => template.sectionType === sectionType,
  );
}
